/**
 * Jotai atoms for conversation sub-navigation in LeadDetailsDrawer.
 *
 * Mirrors the NavStack pattern used by OrderDetailsDrawer (actionItemNav atoms).
 * Single source of truth for view ID, history, selected conversation, and title.
 */

import { atom } from 'jotai';

// ============================================================================
// Types
// ============================================================================

export type ConversationNavViewId = 'root' | 'conversation-thread';

export interface ConversationNavState {
	/** Current view ID */
	viewId: ConversationNavViewId;
	/** Navigation history for back functionality */
	history: ConversationNavViewId[];
	/** Currently selected conversation ID */
	selectedConversationId: string | null;
	/** Title for the current view (used in drawer header) */
	currentTitle: string | null;
}

const defaultConversationNavState: ConversationNavState = {
	viewId: 'root',
	history: [],
	selectedConversationId: null,
	currentTitle: null,
};

// ============================================================================
// Main State Atom
// ============================================================================

export const conversationNavAtom = atom<ConversationNavState>(defaultConversationNavState);

// ============================================================================
// Navigation Actions
// ============================================================================

/** Navigate to a conversation thread view. Pushes history for back navigation. */
export const navigateToConversationAtom = atom(
	null,
	(get, set, params: { conversationId: string; title: string }) => {
		const currentState = get(conversationNavAtom);
		set(conversationNavAtom, {
			viewId: 'conversation-thread',
			history: [...currentState.history, currentState.viewId],
			selectedConversationId: params.conversationId,
			currentTitle: params.title,
		});
	},
);

/** Navigate back to the previous view. Clears selection when returning to root. */
export const navigateBackFromConversationAtom = atom(null, (get, set) => {
	const currentState = get(conversationNavAtom);
	if (currentState.history.length === 0) return;

	const previousViewId = currentState.history[currentState.history.length - 1];
	const newHistory = currentState.history.slice(0, -1);

	set(conversationNavAtom, {
		viewId: previousViewId,
		history: newHistory,
		selectedConversationId: previousViewId === 'root' ? null : currentState.selectedConversationId,
		currentTitle: previousViewId === 'root' ? null : currentState.currentTitle,
	});
});

/** Reset navigation to default state. Used when closing drawer or switching leads. */
export const resetConversationNavAtom = atom(null, (_get, set) => {
	set(conversationNavAtom, defaultConversationNavState);
});

// ============================================================================
// Derived Atoms
// ============================================================================

/** NavStack handler for the NavStack component. */
export const conversationNavHandlerAtom = atom((get) => {
	const { viewId, history } = get(conversationNavAtom);
	return {
		currentViewId: viewId,
		history,
	};
});
